/**
 * axios 封装模块
 * 提供统一的请求方法，支持 GET/POST/PUT/DELETE
 */

import axios from 'axios'
import { Message } from 'element-ui'

// 本地开发走 devServer 代理（相对路径 /api）；发布后拼接后端完整域名（见 public/config.js）
function getBaseURL() {
    const domian = (window._CONFIG && typeof window._CONFIG['domianURL'] === 'string') ? window._CONFIG['domianURL'] : ''
    if (domian) return domian + '/api'
    return process.env.VUE_APP_BASE_API || '/api'
}

// 创建 axios 实例
const service = axios.create({
    baseURL: getBaseURL(),
    timeout: 15000,
    headers: {
        'Content-Type': 'application/json',
        'accept': 'application/json, text/plain, */*',
        'authorization': 'Basic c2FiZXIzOnNhYmVyM19zZWNyZXQ=',
        'X-Requested-With': 'XMLHttpRequest'
    }
})

// 获取当前生效的租户 id（与 auth.js 保持一致）
function getTenantId() {
    try {
        const userStr = localStorage.getItem("saber-userInfo")
        if (userStr) {
            const userInfo = JSON.parse(userStr).content
            if (userInfo.tenant_id) return userInfo.tenant_id
        }
    } catch (e) {
        // ignore
    }
    try {
        const cached = localStorage.getItem('saber-tenant-id')
        if (cached) return cached
    } catch (e) {
        // ignore
    }
    return '000000'
}

// 请求拦截器
service.interceptors.request.use(
    config => {
        // 动态设置 baseURL（防止模块加载时序导致读取不到 domianURL）
        config.baseURL = getBaseURL()
        // 动态设置租户 id（优先当前用户，缺省 000000）
        config.headers['tenant-id'] = getTenantId()
        // 登录接口不需要添加 token
        if (config.url && config.url.includes('/oauth/token')) {
            return config
        }
        
        // 从 localStorage 获取 token
        const userStr = localStorage.getItem("saber-userInfo")
        if (userStr) {
            try {
                const userInfo = JSON.parse(userStr).content
                // 设置 blade-auth token
                if (userInfo.access_token) {
                    config.headers['blade-auth'] = 'bearer ' + userInfo.access_token
                }
            } catch (e) {
                console.error('解析用户信息失败:', e)
            }
        }
        return config
    },
    error => {
        console.error('请求错误:', error)
        return Promise.reject(error)
    }
)

// 响应拦截器
// 401拦截锁，确保token过期/未授权时逻辑只执行一次
let isRedirecting = false
service.interceptors.response.use(
    response => {
        const res = response.data
        return res
    },
    error => {
        console.error('响应错误:', error)
        let message = '网络请求失败'
        if (error.response) {
            switch (error.response.status) {
                case 401:
                    message = '未授权，请重新登录'
                    // 清除 localStorage 中的用户信息（只清一次）
                    if (!isRedirecting) {
                        isRedirecting = true
                        localStorage.removeItem("saber-userInfo")
                        // 重定向到登录页
                        window.close()
                        // window.open('https://web.senoiot.com/login', "_blank")
                        // 1秒后重置标志位，允许用户重新登录后再次触发
                        setTimeout(() => { isRedirecting = false }, 1000)
                    }
                    break
                case 403:
                    message = '拒绝访问'
                    break
                case 404:
                    message = '请求的资源不存在'
                    break
                case 500:
                    message = '服务器内部错误'
                    break
                default:
                    message = error.response.data?.message || '请求失败'
            }
        }
        Message.error(message)
        return Promise.reject(error)
    }
)

/**
 * GET 请求
 * @param {string} url - 请求地址
 * @param {object} params - 查询参数
 * @returns {Promise}
 */
export function get(url, params = {}) {
    return service({
        url,
        method: 'GET',
        params
    })
}

/**
 * POST 请求
 * @param {string} url - 请求地址
 * @param {object} data - 请求数据（body）
 * @param {object} params - URL参数
 * @returns {Promise}
 */
export function post(url, data = null, params = null) {
    const config = {
        url,
        method: 'POST'
    }
    if (data) config.data = data
    if (params) config.params = params
    return service(config)
}

/**
 * PUT 请求
 * @param {string} url - 请求地址
 * @param {object} data - 请求数据
 * @returns {Promise}
 */
export function put(url, data = {}) {
    return service({
        url,
        method: 'PUT',
        data
    })
}

/**
 * DELETE 请求
 * @param {string} url - 请求地址
 * @param {object} params - 查询参数
 * @returns {Promise}
 */
export function del(url, params = {}) {
    return service({
        url,
        method: 'DELETE',
        params
    })
}

export default service
